import React, { useState } from 'react';
import { Card, Button, Input, Select, Textarea, FileUpload } from '../../components';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const GMAIL_COMPOSE = 'https://mail.google.com/mail/?view=cm&fs=1&to=ahmedbinaasim@gmail.com';

const Contact = () => {
  useScrollToTop();
  const formAnim = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    expertise: '',
    experience: '',
    message: '',
  });
  const [resume, setResume] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const expertiseOptions = [
    { value: 'product', label: 'Product' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'gtm', label: 'Go-to-Market / Sales' },
    { value: 'finance', label: 'Finance & Fundraising' },
    { value: 'legal', label: 'Legal & Compliance' },
    { value: 'operations', label: 'Operations' },
    { value: 'design', label: 'Design' },
    { value: 'other', label: 'Other' },
  ];

  const experienceOptions = [
    { value: '1-3', label: '1 – 3 years' },
    { value: '4-7', label: '4 – 7 years' },
    { value: '8-15', label: '8 – 15 years' },
    { value: '15+', label: '15+ years' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResumeSelect = (files: File[]) => {
    if (files.length > 0) setResume(files[0]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resume) {
      alert('Please upload your resume before submitting.');
      return;
    }
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-bg text-text">
        <Header />

        <div className="section-gap pt-24 pb-12">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-success" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h1 className="font-display text-[3rem] leading-none text-text uppercase tracking-wide mb-4">
              Application Received!
            </h1>
            <p className="font-serif-display text-2xl italic text-muted mb-8 max-w-2xl mx-auto">
              Thanks for applying to mentor at Founder Lab. We'll review your background and reach out within 5 business days.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  linkedin: '',
                  expertise: '',
                  experience: '',
                  message: '',
                });
                setResume(null);
              }}
            >
              Submit Another Application
            </Button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-bg text-text">
      <Header />

      <div className="section-gap pt-24 pb-12">
        {/* Hero */}
        <div className="text-center py-8 md:py-12 animate-fade-in">
          <h1 className="font-display text-[3rem] md:text-[5rem] leading-none text-text uppercase tracking-wide mb-6">
            Become a Mentor
          </h1>
          <p className="font-serif-display text-xl md:text-3xl italic text-muted max-w-3xl mx-auto">
            Experienced entrepreneur, investor, or industry professional? Join our mentor network and help the next generation of Pakistan's founders succeed through group sessions, pitch reviews, and phase feedback.
          </p>
        </div>

        <div ref={formAnim.ref} className={`animate-on-scroll ${formAnim.isVisible ? 'visible' : ''}`}>
          <div className="two-col">
            {/* Mentor application form */}
            <Card>
              <h2 className="font-display text-3xl tracking-wide text-text mb-2">Mentor Application</h2>
              <p className="font-serif-display text-lg italic text-muted mb-6">
                Tell us a bit about yourself. All fields are required.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                  />
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Phone / WhatsApp"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+92 300 1234567"
                    required
                  />
                  <Input
                    label="LinkedIn URL"
                    type="url"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/…"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Area of Expertise"
                    name="expertise"
                    value={formData.expertise}
                    onChange={handleChange}
                    options={expertiseOptions}
                    placeholder="Select your primary area"
                    required
                  />
                  <Select
                    label="Years of Experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    options={experienceOptions}
                    placeholder="Select experience range"
                    required
                  />
                </div>

                <Textarea
                  label="Why do you want to mentor?"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your background, what you'd bring to group mentor sessions, and what excites you about supporting early-stage founders."
                  rows={5}
                  required
                />

                {/* Resume upload */}
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Resume / CV <span className="text-muted">(PDF, DOC, DOCX — max 5MB)</span>
                  </label>
                  <FileUpload
                    accept=".pdf,.doc,.docx"
                    multiple={false}
                    maxSize={5}
                    onFileSelect={handleResumeSelect}
                  />
                  {resume && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-success">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>
                        <span className="font-medium">{resume.name}</span> attached
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting…' : 'Submit Application'}
                </Button>
              </form>
            </Card>

            {/* Other queries card */}
            <div className="section-gap">
              <Card>
                <div className="flex flex-col h-full">
                  <div className="flex-shrink-0 w-14 h-14 bg-accent-soft rounded-xl flex items-center justify-center mb-5">
                    <svg className="w-7 h-7 text-chart" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl tracking-wide text-text mb-3">
                    Not looking to mentor?
                  </h3>
                  <p className="font-serif-display text-lg italic text-muted mb-6 leading-relaxed">
                    For partnerships, press, investor introductions, or any other query — send us an email directly and we'll get back within one business day.
                  </p>
                  <a
                    href={GMAIL_COMPOSE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-chart text-white px-6 py-3 rounded-lg font-medium hover:bg-chart/90 transition-all duration-200 shadow-lg hover:shadow-chart/20"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
                    </svg>
                    Email Us
                  </a>
                  <p className="mt-4 text-xs text-muted">
                    Opens Gmail compose in a new tab with our address pre-filled.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
